// product/AddressMap.jsx
// Three-way address input:
//   1. Google Places autocomplete search bar
//   2. Tap on map to place marker
//   3. "Compartir mi ubicación" button (browser Geolocation API)
//
// Props:
//   onLocationChange({ address, district, lat, lng }) — called whenever location changes
//   googleMapsLoaded — boolean, true when the Maps script is ready

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Locate, Search, X } from "lucide-react";
import { brand } from "../shared/brand";

// Leaflet is loaded via CDN in index.html OR dynamically here.
// We import it lazily to avoid SSR issues.
let L = null;

const LIMA_CENTER = [-12.046374, -77.042793];
const DEFAULT_ZOOM = 13;

// Lima districts for fallback / override dropdown
const LIMA_DISTRICTS = [
  "Ancón","Ate","Barranco","Bellavista","Breña","Carabayllo","Carmen de la Legua",
  "Cercado de Lima","Chorrillos","Comas","El Agustino","Independencia","Jesús María",
  "La Molina","La Perla","La Punta","Lince","Los Olivos","Magdalena del Mar",
  "Miraflores","Pueblo Libre","Puente Piedra","Rímac","San Borja","San Isidro",
  "San Juan de Lurigancho","San Juan de Miraflores","San Martín de Porres","San Miguel",
  "Santa Anita","Santa Rosa","Santiago de Surco","Surquillo","Ventanilla",
  "Villa El Salvador","Villa María del Triunfo",
].sort((a, b) => a.localeCompare(b, "es"));

export function AddressMap({ onLocationChange, googleMapsLoaded }) {
  const mapRef       = useRef(null); // DOM node
  const leafletMap   = useRef(null); // L.Map instance
  const markerRef    = useRef(null); // L.Marker instance
  const autocomplete = useRef(null); // Google Places Autocomplete instance
  const inputRef     = useRef(null); // Search input DOM node

  const [address,  setAddress]  = useState("");
  const [district, setDistrict] = useState("");
  const [locating, setLocating] = useState(false);
  const [error,    setError]    = useState("");

  // ── Init Leaflet map ──────────────────────────────────────────────────────
  useEffect(() => {
    // Dynamically import Leaflet only on client
    import("leaflet").then(leaflet => {
      L = leaflet.default || leaflet;

      // Leaflet default icon fix (webpack strips the default images)
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      if (!mapRef.current || leafletMap.current) return;

      leafletMap.current = L.map(mapRef.current, {
        center: LIMA_CENTER,
        zoom:   DEFAULT_ZOOM,
        zoomControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(leafletMap.current);

      // Tap on map → move marker
      leafletMap.current.on("click", e => {
        placeMarker(e.latlng.lat, e.latlng.lng, true);
      });
    });

    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
  }, []);

  // ── Init Google Places Autocomplete ──────────────────────────────────────
  useEffect(() => {
    if (!googleMapsLoaded || !inputRef.current || !window.google) return;

    autocomplete.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "pe" },
      fields: ["geometry", "formatted_address", "address_components"],
    });

    autocomplete.current.addListener("place_changed", () => {
      const place = autocomplete.current.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const addr = place.formatted_address || "";

      // Extract district from address_components
      const districtComp = place.address_components?.find(c =>
        c.types.includes("sublocality_level_1") ||
        c.types.includes("sublocality") ||
        c.types.includes("locality")
      );
      const detectedDistrict = districtComp?.long_name || "";

      setAddress(addr);
      setDistrict(detectedDistrict);
      placeMarker(lat, lng, false);
      notify(addr, detectedDistrict, lat, lng);
    });
  }, [googleMapsLoaded]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  function placeMarker(lat, lng, reverseGeocode) {
    if (!L || !leafletMap.current) return;

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable: true })
        .addTo(leafletMap.current);

      markerRef.current.on("dragend", e => {
        const pos = e.target.getLatLng();
        placeMarker(pos.lat, pos.lng, true);
      });
    }

    leafletMap.current.panTo([lat, lng]);

    if (reverseGeocode && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const addr = results[0].formatted_address;
          const distComp = results[0].address_components?.find(c =>
            c.types.includes("sublocality_level_1") ||
            c.types.includes("sublocality") ||
            c.types.includes("locality")
          );
          const dist = distComp?.long_name || district;
          setAddress(addr);
          if (inputRef.current) inputRef.current.value = addr;
          notify(addr, dist, lat, lng);
        } else {
          notify(address, district, lat, lng);
        }
      });
    } else {
      notify(address, district, lat, lng);
    }
  }

  function notify(addr, dist, lat, lng) {
    onLocationChange({ address: addr, district: dist, lat, lng });
  }

  function handleShareLocation() {
    if (!navigator.geolocation) {
      setError("Tu navegador no soporta geolocalización.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocating(false);
        placeMarker(pos.coords.latitude, pos.coords.longitude, true);
      },
      () => {
        setLocating(false);
        setError("No pudimos obtener tu ubicación. Verifica los permisos.");
      },
      { timeout: 10000 }
    );
  }

  function handleDistrictChange(val) {
    setDistrict(val);
    notify(address, val, markerRef.current?.getLatLng()?.lat, markerRef.current?.getLatLng()?.lng);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-3">

      {/* Search bar */}
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <Search className="size-4 text-slate-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Busca tu dirección..."
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          onChange={e => setAddress(e.target.value)}
        />
        {address && (
          <button onClick={() => { setAddress(""); if (inputRef.current) inputRef.current.value = ""; }}>
            <X className="size-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Share location button */}
      <button
        onClick={handleShareLocation}
        disabled={locating}
        className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
      >
        <Locate className="size-4" style={{ color: brand.accentGreen }} />
        {locating ? "Obteniendo ubicación..." : "Usar mi ubicación actual"}
      </button>

      {/* Map */}
      <div
        ref={mapRef}
        className="rounded-2xl overflow-hidden border border-slate-200"
        style={{ height: 240 }}
      />

      <p className="text-xs text-slate-400 flex items-center gap-1">
        <MapPin className="size-3 shrink-0" />
        Toca el mapa para ajustar el pin. Puedes arrastrarlo también.
      </p>

      {/* District override */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
          Distrito
        </label>
        <select
          value={district}
          onChange={e => handleDistrictChange(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none"
        >
          <option value="">Seleccionar distrito</option>
          {LIMA_DISTRICTS.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {error && (
        <p className="text-xs text-red-500 font-semibold">{error}</p>
      )}
    </div>
  );
}